/**
 * Tâche 1135 (Eric 2026-06-09) — Paires français/anglais modifiables.
 *
 * Les 18 paires métier vivaient en dur dans le code : ajouter « courtier
 * hypothécaire (mortgage broker) » demandait un déploiement. Elles sont
 * maintenant un socle que l'organisation complète, corrige et peut désactiver.
 *
 * Ces paires alimentent la détection de doublons, les renvois EN/FR et la
 * recherche bilingue : une paire ajoutée ici agit partout immédiatement.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, SpinnerGap, Plus, Trash, ArrowsLeftRight, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TranslationPairsModal({ open, onClose, onChanged, canEdit = false }) {
  const { tr } = useLang();
  const [loading, setLoading] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [fr, setFr] = useState("");
  const [en, setEn] = useState("");
  const [saving, setSaving] = useState(false);
  // Le toast disparaît en trois secondes : pour un refus de formulaire, le
  // message doit rester sous les champs jusqu'à correction.
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [accepting, setAccepting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pairsRes, sugRes] = await Promise.all([
        axios.get(`${API}/api/jargon/translation-pairs`, { withCredentials: true }),
        axios.get(`${API}/api/jargon/translation-pairs/suggestions`, {
          withCredentials: true,
        }),
      ]);
      setPairs(pairsRes.data?.pairs || []);
      // Décochées d'avance, contrairement aux renvois de la tâche 1131 : une
      // parenthèse peut être française (« Code (assurance) »), et une fausse
      // équivalence modifie silencieusement les doublons et la recherche dans
      // toute l'application. C'est plus lourd à défaire qu'un renvoi.
      setSuggestions(sugRes.data?.suggestions || []);
      setPicked(new Set());
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  // Tâche 1138 — Équivalences déjà écrites entre parenthèses dans les titres
  // (« Fonds Distinct (Segregated Fund) ») mais absentes des équivalences :
  // un rapprochement écrit noir sur blanc que la recherche ignorait.
  const acceptSuggestions = async () => {
    if (picked.size === 0) return;
    setAccepting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/translation-pairs/suggestions/accept`,
        { keys: [...picked] },
        { withCredentials: true },
      );
      toast.success(`${data.added_count} équivalence(s) ajoutée(s).`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setAccepting(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const addPair = async () => {
    if (!fr.trim() || !en.trim()) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${API}/api/jargon/translation-pairs`,
        { fr: fr.trim(), en: en.trim() },
        { withCredentials: true },
      );
      toast.success(`« ${fr.trim()} » ↔ « ${en.trim()} » enregistrée.`);
      setFr("");
      setEn("");
      await load();
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setError(detail);
      toast.error(tr("Erreur : ", "Error: ") + detail);
    } finally {
      setSaving(false);
    }
  };

  const removePair = async (pair) => {
    try {
      await axios.delete(
        `${API}/api/jargon/translation-pairs/${encodeURIComponent(pair.key)}`,
        { withCredentials: true },
      );
      toast.success(`« ${pair.fr} » ↔ « ${pair.en} » retirée.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const resetPair = async (pair) => {
    try {
      await axios.post(
        `${API}/api/jargon/translation-pairs/${encodeURIComponent(pair.key)}/reset`,
        {},
        { withCredentials: true },
      );
      toast.success(`« ${pair.fr} » rétablie telle que livrée.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="translation-pairs-modal"
    >
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
          <h2 className="text-base font-semibold text-zinc-900">
            {tr("Équivalences français / anglais", "French / English equivalences")}
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-400 transition-colors hover:text-zinc-900"
            data-testid="translation-pairs-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 text-xs text-zinc-700">
          {tr(
            "Ces équivalences servent partout : détection de doublons, renvois EN/FR et recherche bilingue. Une paire ajoutée ici agit immédiatement, sans déploiement. Les paires livrées avec Knoria peuvent être corrigées ou retirées — le retrait ne vaut que pour votre organisation.",
            "These equivalences are used everywhere: duplicate detection, EN/FR cross-references and bilingual search. A pair added here takes effect immediately, with no deployment. Pairs shipped with Knoria can be corrected or removed — a removal applies to your organisation only.",
          )}
        </div>

        {canEdit && (
          <div className="px-4 py-3 border-b border-zinc-200">
            <div className="flex flex-wrap items-center gap-2">
            <input
              value={fr}
              onChange={(e) => {
                setFr(e.target.value);
                setError("");
              }}
              placeholder={tr("Terme français", "French term")}
              className="flex-1 min-w-[10rem] h-9 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="translation-pairs-fr-input"
            />
            <ArrowsLeftRight size={16} className="text-zinc-400" />
            <input
              value={en}
              onChange={(e) => {
                setEn(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") addPair();
              }}
              placeholder={tr("Équivalent anglais", "English equivalent")}
              className="flex-1 min-w-[10rem] h-9 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="translation-pairs-en-input"
            />
            <button
              onClick={addPair}
              disabled={saving || !fr.trim() || !en.trim()}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#002FA7] text-white text-sm font-semibold transition-colors hover:bg-[#00248a] disabled:opacity-40"
              data-testid="translation-pairs-add"
            >
              <Plus size={14} />
              {saving ? "Ajout…" : "Ajouter"}
            </button>
            </div>
            {error && (
              <p className="mt-2 text-xs text-rose-600" data-testid="translation-pairs-error">
                {error}
              </p>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {/* Tâche 1138 — Suggestions repérées dans les parenthèses des
              titres. Cochées d'avance mais jamais enregistrées sans clic. */}
          {canEdit && !loading && suggestions.length > 0 && (
            <div className="border-b-2 border-amber-200 bg-amber-50/60" data-testid="pair-suggestions">
              <div className="px-4 py-2.5 flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold text-amber-900">
                  {suggestions.length} {tr("équivalence(s) déjà écrite(s) dans vos titres", "equivalence(s) already written in your titles")}
                </span>
                <span className="text-[11px] text-amber-800">
                  {tr(
                    "repérées entre parenthèses — vérifiez : certaines parenthèses sont françaises",
                    "spotted inside brackets — check them: some brackets hold French",
                  )}
                </span>
                <button
                  onClick={() => setPicked(new Set(suggestions.map((s) => s.key)))}
                  className="text-[11px] text-amber-900 underline transition-colors hover:text-amber-950"
                  data-testid="pair-suggestions-select-all"
                >
                  Tout cocher
                </button>
                <button
                  onClick={acceptSuggestions}
                  disabled={accepting || picked.size === 0}
                  className="ml-auto text-xs font-semibold px-3 py-1.5 rounded bg-amber-700 text-white transition-colors hover:bg-amber-800 disabled:opacity-40"
                  data-testid="pair-suggestions-accept"
                >
                  {accepting
                    ? "Ajout…"
                    : picked.size === 0
                      ? tr("Cochez ce qui est juste", "Tick what is right")
                      : tr(`Ajouter les ${picked.size} cochée(s)`, `Add the ${picked.size} ticked`)}
                </button>
              </div>
              <ul className="pb-2">
                {suggestions.map((s) => {
                  const toggle = () =>
                    setPicked((prev) => {
                      const next = new Set(prev);
                      if (next.has(s.key)) next.delete(s.key);
                      else next.add(s.key);
                      return next;
                    });
                  return (
                  <li
                    key={s.key}
                    className="px-4 py-1.5 flex items-center gap-2.5 cursor-pointer transition-colors hover:bg-amber-100/60"
                    onClick={toggle}
                    data-testid={`pair-suggestion-${s.key}`}
                  >
                    {/* Tâche 1159 — La case elle-même doit cocher. Elle
                        arrêtait la propagation SANS traiter le clic : seul le
                        clic sur le mot fonctionnait. */}
                    <input
                      type="checkbox"
                      checked={picked.has(s.key)}
                      onChange={toggle}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-pointer"
                      data-testid={`pair-suggestion-checkbox-${s.key}`}
                    />
                    <span className="text-sm text-zinc-900 font-medium">{s.fr}</span>
                    <ArrowsLeftRight size={12} className="text-amber-600" />
                    <span className="text-sm text-zinc-700">{s.en}</span>
                    <span className="text-[10px] text-amber-700 truncate">
                      d'après « {s.source_term} »
                    </span>
                  </li>
                  );
                })}
              </ul>
            </div>
          )}
          {loading ? (
            <div className="py-16 text-center text-sm text-zinc-500">
              <SpinnerGap size={20} className="inline animate-spin mr-2" />
              Chargement…
            </div>
          ) : pairs.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-500" data-testid="translation-pairs-empty">
              {tr("Aucune équivalence — ajoutez la première paire ci-dessus.", "No equivalence — add the first pair above.")}
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="translation-pairs-list">
              {pairs.map((p) => (
                <li
                  key={p.key}
                  className={`px-4 py-2 flex items-center gap-3 transition-colors ${p.disabled ? "bg-zinc-50/60" : "hover:bg-zinc-50"}`}
                  data-testid={`translation-pairs-row-${p.key}`}
                >
                  <div
                    className={`flex-1 min-w-0 text-sm flex items-center gap-2 flex-wrap ${p.disabled ? "text-zinc-400 line-through decoration-zinc-300" : "text-zinc-900"}`}
                  >
                    <span className="font-medium">{p.fr}</span>
                    <ArrowsLeftRight size={12} className="text-zinc-400" />
                    <span className={p.disabled ? "" : "text-zinc-600"}>{p.en}</span>
                    {p.builtin && (
                      <span className="px-1 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-500 no-underline">
                        {tr("livrée", "shipped")}
                      </span>
                    )}
                    {p.customized && (
                      <span className="px-1 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 no-underline">
                        {tr("modifiée", "edited")}
                      </span>
                    )}
                    {p.disabled && (
                      <span className="px-1 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 no-underline">
                        {tr("retirée", "removed")}
                      </span>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      {p.builtin && (p.customized || p.disabled) && (
                        <button
                          onClick={() => resetPair(p)}
                          title={tr("Rétablir la paire livrée avec Knoria", "Restore the pair shipped with Knoria")}
                          className="text-zinc-400 transition-colors hover:text-[#002FA7]"
                          data-testid={`translation-pairs-reset-${p.key}`}
                        >
                          <ArrowCounterClockwise size={16} />
                        </button>
                      )}
                      {!p.disabled && (
                        <button
                          onClick={() => removePair(p)}
                          title={tr("Retirer cette équivalence", "Remove this equivalence")}
                          className="text-zinc-400 transition-colors hover:text-rose-600"
                          data-testid={`translation-pairs-delete-${p.key}`}
                        >
                          <Trash size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-zinc-200">
          <span className="text-xs text-zinc-400" data-testid="translation-pairs-count">
            {pairs.filter((p) => !p.disabled).length} équivalence(s) active(s)
          </span>
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white transition-colors hover:bg-zinc-50"
            data-testid="translation-pairs-done"
          >
            {tr("Fermer", "Close")}
          </button>
        </div>
      </div>
    </div>
  );
}
